import React, { useState } from "react"
import { graphql, Link } from "gatsby"
import styled from "styled-components"
import { media } from "src/styles/breakpoints"
import Seo from "src/components/SEO"
import BaseContainer from "src/components/Base/BaseContainer"
import ResourcesSearchForm from "src/components/ResourcesSearchForm"
import ResourcesFilters from "src/components/ResourcesFilters"
import ResourcesHeader from "src/components/ResourcesHeader"
import ResourcesListHeader from "src/components/ResourcesListHeader"
import ResourceTile from "src/components/ResourceTile"
import staticResources from "../../data/resources"
import { slugify } from "src/utils/helpers"
import { get } from "lodash"

export const pageQuery = graphql`
  {
    allStrapiResource(sort: { fields: createdAt, order: DESC }) {
      edges {
        node {
          Title
          category {
            id
          }
          country {
            name
          }
          date_of_capture
          content_type
          number_of_files
          createdAt(formatString: "MMMM D, YYYY")
          Preview {
            url
            localFile {
              childImageSharp {
                gatsbyImageData(height: 143, quality: 80, layout: FIXED)
              }
            }
          }
        }
      }
    }
  }
`

const ResourcesCategory = (props) => {
  const {
    id,
    name,
    description,
    slug,
    icon,
    color,
    parent,
    children,
    allCategories,
  } = props.pageContext

  const [filter, setFilter] = useState({
    typeVideos: true,
    typePhotos: true,
    country: "",
    year: "",
  })

  const [sort, setSort] = useState("Date added – newest first")

  const nestedCategories = getNestedCategories(id)

  const categoryResources = getResourcesFromCategories(nestedCategories)

  const resources = categoryResources.filter((r) => {
    let result = true
    if (filter.year && !r.date_of_capture) {
      return false;
    }
    if (
      (!filter.typeVideos && r.content_type === "Video") ||
      (!filter.typePhotos && r.content_type === "Picture") ||
      (filter.country && (!r.country || r.country.name !== filter.country)) ||
      (filter.year && r.date_of_capture && r.date_of_capture.substring(0, 4) !== filter.year)
    ) {
      result = false
    }
    return result
  })

  if (sort === "Date added – oldest first") {
    resources.reverse()
  } else if (sort === "Name – A-Z") {
    resources.sort((a, b) => a.Title.localeCompare(b.Title))
  } else if (sort === "Name – Z-A") {
    resources.sort((a, b) => b.Title.localeCompare(a.Title))
  }

  const rootPath = "/resources"
  const parentPath = parent.length
    ? parent.map((p) => p.slug).join("/") + "/"
    : ""
  const currentPath = `${rootPath}/${parentPath}${slug}`

  const iconColor = color || (parent[0] && parent[0].color)
  const iconName = icon || (parent[0] && parent[0].icon) || "camera"

  const breadcrumbs = [
    {
      name: "Resource library",
      path: `${rootPath}#categories`,
    },
    ...parent.map(({ name, slug }, index) => {
      const path = `${rootPath}/${index > 0 ? parent[0].slug + "/" : ""}${slug}`
      return {
        name,
        path,
      }
    }),
    {
      name,
      path: currentPath,
    },
  ]

  const showTypeFilter = categoryResources.find((r) =>
    ["Video", "Picture"].includes(r.content_type)
  )

  const currentNavigationItem = (
    <NavigationListItem key={-1}>
      <NavigationListLink to={currentPath}>
        <NavigationListLinkTitle>{name}</NavigationListLinkTitle>
      </NavigationListLink>
      {children && (
        <NavigationList>
          {children.map(({ id, name }, index) => {
            const count = getResourcesFromCategories(
              getNestedCategories(id)
            ).length
            return (
              count > 0 && (
                <NavigationListItem key={index}>
                  <NavigationListLink
                    to={`${rootPath}/${parentPath}${slug}/${slugify(name)}`}
                  >
                    <NavigationListLinkTitle>{name}</NavigationListLinkTitle>
                    <NavigationListLinkCount>{count}</NavigationListLinkCount>
                  </NavigationListLink>
                </NavigationListItem>
              )
            )
          })}
        </NavigationList>
      )}
    </NavigationListItem>
  )

  function getNestedCategories(categoryId) {
    const categories = [categoryId]
    for (let i = 0; i < 5; i++) {
      allCategories.forEach((category) => {
        if (
          category.parent_category &&
          categories.includes(category.parent_category.id) &&
          !categories.includes(category.strapiId)
        ) {
          categories.push(category.strapiId)
        }
      })
    }
    return categories
  }

  function getResourcesFromCategories(categories) {
    return props.data.allStrapiResource.edges
      .map((r) => r.node)
      .filter((r) => r.category && categories.includes(r.category.id))
  }

  function getCountries() {
    let countries = []
    categoryResources.forEach((r) => {
      if (r.country && !countries.includes(r.country.name)) {
        countries.push(r.country.name)
      }
    })
    countries.sort()
    return countries
  }

  function getYears() {
    let years = []
    categoryResources.forEach((r) => {
      if (r.date_of_capture && r.date_of_capture.substring(0, 4) && !years.includes(r.date_of_capture.substring(0, 4))) {
        years.push(r.date_of_capture.substring(0, 4))
      }
    })
    years.sort().reverse()
    return years
  }

  function updateFilter(filterName, event) {
    const newFilter = { ...filter }
    if (filterName === "typeVideos") {
      newFilter.typeVideos = event.target.checked
    } else if (filterName === "typePhotos") {
      newFilter.typePhotos = event.target.checked
    } else if (filterName === "country") {
      newFilter.country = event.target.value ? event.target.value : ""
    } else if (filterName === "year") {
      newFilter.year = event.target.value ? parseInt(event.target.value) : ""
    }
    setFilter(newFilter)
  }

  function onSortChange(name, value) {
    setSort(value)
  }

  const navigationTree = (
    <NavigationList>
      {staticResources.map((category, index) => {
        if (parent.length > 0 && parent[0].name === category.name) {
          return (
            <NavigationListItem key={index}>
              <NavigationListLink to={`${rootPath}/${parent[0].slug}`}>
                <NavigationListLinkTitle>
                  {parent[0].name}
                </NavigationListLinkTitle>
              </NavigationListLink>
              <NavigationList>
                {allCategories
                  .filter(
                    (category) =>
                      category.parent_category &&
                      category.parent_category.id === parent[0].id &&
                      category.resources.length > 0
                  )
                  .map((category, index) => {
                    return category.name === name ? (
                      currentNavigationItem
                    ) : (
                      <NavigationListItem key={index}>
                        <NavigationListLink
                          to={`${rootPath}/${parent[0].slug}/${slugify(
                            category.name
                          )}`}
                        >
                          <NavigationListLinkTitle>
                            {category.name}
                          </NavigationListLinkTitle>
                        </NavigationListLink>
                        {parent.length > 1 &&
                        category.name === parent[1].name ? (
                          <NavigationList>
                            {allCategories
                              .filter(
                                (category) =>
                                  category.parent_category &&
                                  category.parent_category.id ===
                                    parent[1].id &&
                                  category.resources.length > 0
                              )
                              .map((category, index) => {
                                return category.name === name ? (
                                  currentNavigationItem
                                ) : (
                                  <NavigationListItem key={index}>
                                    <NavigationListLink
                                      to={`${rootPath}/${parent[0].slug}/${
                                        parent[1].slug
                                      }/${slugify(category.name)}`}
                                    >
                                      <NavigationListLinkTitle>
                                        {category.name}
                                      </NavigationListLinkTitle>
                                    </NavigationListLink>
                                    {parent.length > 1 &&
                                    category.name === parent[1].name ? (
                                      <NavigationList>
                                        {currentNavigationItem}
                                      </NavigationList>
                                    ) : (
                                      ""
                                    )}
                                  </NavigationListItem>
                                )
                              })}
                          </NavigationList>
                        ) : (
                          ""
                        )}
                      </NavigationListItem>
                    )
                  })}
              </NavigationList>
            </NavigationListItem>
          )
        } else {
          if (category.name === name) {
            return currentNavigationItem
          } else {
            return (
              <NavigationListItem key={index}>
                <NavigationListLink to={`${rootPath}/${category.slug}`}>
                  <NavigationListLinkTitle>
                    {category.name}
                  </NavigationListLinkTitle>
                </NavigationListLink>
                {parent[0] && parent[0].name === category.name && (
                  <NavigationList>{currentNavigationItem}</NavigationList>
                )}
              </NavigationListItem>
            )
          }
        }
      })}
    </NavigationList>
  )

  return (
    <Wrapper>
      <Seo title={name} />
      <Container wide>
        <Sidebar>
          <SearchForm />
          <Navigation>{navigationTree}</Navigation>
          {categoryResources.length > 0 && (
            <ResourcesFilters
              showTypeFilter={showTypeFilter}
              countryList={getCountries()}
              yearList={getYears()}
              onUpdateFilter={updateFilter}
            />
          )}
        </Sidebar>
        <Main>
          <ResourcesHeader
            title={name}
            description={description}
            breadcrumbs={breadcrumbs}
            icon={{
              color: iconColor,
              name: iconName,
            }}
          />
          <ChildList>{currentNavigationItem}</ChildList>
          <ResourceList>
            <ResourcesListHeader
              resources={resources}
              onSortChange={onSortChange}
            />
            <ResourceListItems>
              {resources.map(
                (
                  {
                    Title,
                    category,
                    country,
                    date_of_capture,
                    content_type,
                    number_of_files,
                    createdAt,
                    Preview,
                  },
                  index
                ) => (
                  <ResourceTile
                    path={`${currentPath
                      .split("/")
                      .slice(0, 3)
                      .join("/")}/${slugify(Title)}`}
                    title={Title}
                    type={content_type}
                    numberOfFiles={number_of_files}
                    country={country && country.name}
                    year={date_of_capture ? date_of_capture.substring(0, 4) : ""}
                    dateAdded={createdAt}
                    imageSharp={get(Preview[0], "localFile.childImageSharp")}
                    imageSharpSequence={
                      Preview.length > 1 &&
                      Preview.slice(0, 10).map((p) =>
                        get(p, "localFile.childImageSharp")
                      )
                    }
                    key={index}
                  />
                )
              )}
            </ResourceListItems>
          </ResourceList>
        </Main>
      </Container>
    </Wrapper>
  )
}

/* eslint-disable limited-exports-page-templates */
export const Wrapper = styled.div`
  padding-top: 180px;
  padding-bottom: 160px;

  ${media.tablet} {
    padding-top: 250px;
    padding-bottom: 220px;
    position: relative;
    background: linear-gradient(
      to right,
      #f3f6f6 250px,
      transparent 250px,
      transparent
    );

    &::before,
    &::after {
      content: "";
      width: 100%;
      height: 300px;
      position: absolute;
      left: 0;
      pointer-events: none;
    }

    &::before {
      top: 0;
      background: linear-gradient(
        rgba(255, 255, 255, 1),
        rgba(255, 255, 255, 0)
      );
    }

    &::after {
      bottom: 0;
      background: linear-gradient(
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 1)
      );
    }
  }

  ${media.desktop} {
    background: linear-gradient(
      to right,
      #f3f6f6 340px,
      transparent 340px,
      transparent
    );
  }

  ${media.desktopMedium} {
    background: linear-gradient(
      to right,
      #f3f6f6 410px,
      transparent 410px,
      transparent
    );
  }

  @media (min-width: 1360px) {
    background: linear-gradient(
      to right,
      #f3f6f6 calc(50% - 270px),
      transparent calc(50% - 270px),
      transparent
    );
  }
`

export const Container = styled(BaseContainer)`
  display: flex;
  flex-direction: column-reverse;

  ${media.tablet} {
    flex-direction: row;
  }
`

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin-top: 100px;

  ${media.tablet} {
    display: block;
    width: 180px;
    margin-top: 0;
  }

  ${media.desktop} {
    width: 240px;
  }

  ${media.desktopMedium} {
    width: 290px;
  }
`

export const SearchForm = styled(ResourcesSearchForm)`
  margin-top: 70px;

  ${media.tablet} {
    margin-top: 0;
    margin-bottom: 70px;
  }
`

export const Navigation = styled.div`
  display: none;
  margin-bottom: 60px;
  font-size: 15px;
  line-height: 1.6;
  color: ${(p) => p.theme.color.textLight};

  ${media.tablet} {
    display: block;
  }
`

export const NavigationList = styled.ul`
  &:not(:first-child) {
    margin-left: 22px;
  }
`

export const NavigationListItem = styled.li``

export const NavigationListLink = styled(Link)`
  display: flex;
  justify-content: space-between;

  &:hover {
    color: ${(p) => p.theme.color.primary};
  }

  &[aria-current],
  &.-active {
    font-weight: 600;
    color: #000;
  }
`

export const NavigationListLinkTitle = styled.span`
  transition: color 0.15s;
`

const NavigationListLinkCount = styled.span`
  opacity: 0.5;
  font-weight: ${(p) => p.theme.basefontWeight};
  font-family: ${(p) => p.theme.font.mono};
  color: ${(p) => p.theme.color.textLight};
`

export const Main = styled.div`
  position: relative;
  z-index: 1;

  ${media.tablet} {
    flex: 1;
    margin-left: 70px;
  }

  ${media.desktop} {
    margin-left: 100px;
  }

  ${media.desktopMedium} {
    margin-left: 140px;
  }
`

const ChildList = styled.div`
  margin-bottom: 60px;
  // font-size: 13px;

  li {
    display: inline-block;
    margin-right: 20px;
    list-style: none;
  }

  a {
    display: inline-block;
  }

  span:last-child {
    margin-left: 10px;
  }

  > li > a {
    display: none;
  }

  ul:not(:first-child) {
    margin-left: 0;
  }

  ${media.tablet} {
    display: none;
  }
`

export const ResourceList = styled.div``

export const ResourceListItems = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    width: calc((100% - 20px) / 2);

    &:nth-child(n + 3) {
      margin-top: 30px;
    }

    &:not(:nth-child(2n + 1)) {
      margin-left: 20px;
    }
  }

  ${media.desktop} {
    > * {
      width: calc((100% - 30px * 2) / 3);

      &:nth-child(n + 3) {
        margin-top: 0;
      }

      &:nth-child(n + 4) {
        margin-top: 30px;
      }

      &:not(:nth-child(2n + 1)) {
        margin-left: 0;
      }

      &:not(:nth-child(3n + 1)) {
        margin-left: 30px;
      }
    }
  }
`

export default ResourcesCategory
